import _ from 'lodash';
import { addAssetOrItem, setIsLoading } from '@/assetGroupEditor/assetGroup.actions';
import { editAssetGroup, validateItemToAdd } from '@/assetGroupEditor/assetGroup.utilities';
import { IconWithManagedSpinner } from '@/core/IconWithManagedSpinner.atom';
import {
  sqAssetGroupStore,
  sqLayoutStore,
  sqLicenseManagementStore,
  sqScalingTableDefinitionStore,
  sqWorksheetStore,
} from '@/core/core.stores';
import { useFluxPath } from '@/core/hooks/useFluxPath.hook';
import { CREATED_BY_SEEQ_WORKBENCH } from '@/main/app.constants';
import { SeeqNames } from '@/main/app.constants.seeqnames';
import { setAutoFocusTableDefinitionTab } from '@/main/layout.actions';
import { navigateToTreeBuilderPage } from '@/main/routing.utilities';
import { isItemTable, isScalingTable } from '@/search/search.utilities';
import { canWriteItem } from '@/services/authorization.service';
import { editTableDefinition } from '@/tableDefinitionEditor/tableDefinition.utilities';
import { isAsset, isTableDefinition } from '@/utilities/utilities';
import { setDisplayAssetGroupEditor } from '@/worksheet/worksheet.actions';
import type * as React from 'react';
import type { SelectedSearchItem } from '@/search/search.types';

interface SearchResultAssetIconProps {
  item: SelectedSearchItem & { isAssetGroupItem?: boolean };
  isAssetGroupView: boolean;
}

export const SearchResultAssetIcons = ({ item, isAssetGroupView }: SearchResultAssetIconProps) => {
  const storeAssets = useFluxPath(sqAssetGroupStore, () => sqAssetGroupStore.assets);
  const isTableDefinitionView = useFluxPath(sqWorksheetStore, () => sqWorksheetStore.displayTableDefinitionView);
  const isEditingAssetGroup = useFluxPath(sqAssetGroupStore, () => sqAssetGroupStore.id === item.id);
  const scalingTableEditorVisible = useFluxPath(sqLayoutStore, () => sqLayoutStore.tableDefinitionEditorVisible);
  const isEditingTableDefinition =
    useFluxPath(sqScalingTableDefinitionStore, () => sqScalingTableDefinitionStore.id === item.id) &&
    isTableDefinitionView;
  const isEditTableDefinitionAllowed = isTableDefinition(item);
  const isEditAllowed =
    isEditTableDefinitionAllowed ||
    (isAsset(item) &&
      sqLicenseManagementStore.hasAssetGroups() &&
      canWriteItem(item) &&
      (_.find(item.properties, { name: SeeqNames.Properties.TreeType })?.value === CREATED_BY_SEEQ_WORKBENCH ||
        item.isAssetGroupItem) &&
      _.isEmpty(item.ancestors));

  const disableEdit = isEditingAssetGroup || (isEditingTableDefinition && scalingTableEditorVisible);

  const addAsset = (addMatchingItemsOnly = false, addChildrenOfItemsOfItems = false) => {
    setIsLoading(true);
    const promise = validateItemToAdd({ item: { type: item.type || '' } })
      ? addAssetOrItem({
          assetOrItem: item as SelectedSearchItem & {
            ancestors?: Array<{ type?: string; name?: string; id?: string }>;
          },
          addMatchingItemsOnly,
          addItemsOfItemsChildren: addChildrenOfItemsOfItems,
        })
      : Promise.resolve();

    return promise.finally(() => setIsLoading(false));
  };

  const loadTableDefinitionForEdit = async (event: React.MouseEvent | React.KeyboardEvent): Promise<void> => {
    if (isItemTable(item)) {
      navigateToTreeBuilderPage({ tableId: item.id, event });
    } else if (isScalingTable(item)) {
      setAutoFocusTableDefinitionTab(true);
      await editTableDefinition(item, 'SCALING');
    }
  };

  const loadAssetGroupForEdit = (): Promise<void> => {
    setDisplayAssetGroupEditor(true);
    return editAssetGroup(item);
  };

  let tableDefinitionEditTooltip: string | undefined;
  if (isEditTableDefinitionAllowed) {
    if (!isEditingTableDefinition && isScalingTable(item)) {
      tableDefinitionEditTooltip = 'SEARCH_DATA.EDIT_SCALING_TABLE_TOOLTIP';
    } else if (isItemTable(item)) {
      tableDefinitionEditTooltip = 'SEARCH_DATA.EDIT_ITEM_TABLE_TOOLTIP';
    }
  }
  const tooltip =
    !isEditingAssetGroup && isAsset(item) ? 'SEARCH_DATA.EDIT_ASSET_GROUP_TOOLTIP' : tableDefinitionEditTooltip;
  let testId = '';
  if (isAsset(item)) {
    testId = 'editAssetGroupIcon';
  } else if (isEditTableDefinitionAllowed && isScalingTable(item)) {
    testId = 'editScalingTableIcon';
  } else if (isEditTableDefinitionAllowed && isItemTable(item)) {
    testId = 'editItemTableDefinitionIcon';
  }
  return (
    <>
      {isEditAllowed && (
        <IconWithManagedSpinner
          icon="fa-pencil"
          testId={testId}
          disabled={disableEdit}
          large={true}
          extraClassNames="searchBtn"
          action={isAsset(item) ? loadAssetGroupForEdit : loadTableDefinitionForEdit}
          actionOnEnter={true}
          tooltip={tooltip || ''}
          tooltipDelay={500}
        />
      )}

      {isAssetGroupView && (
        <>
          {/* assets and "items of items'" parents */}
          {(isAsset(item) || (!isAsset(item) && item.hasActiveChildren)) && (
            <IconWithManagedSpinner
              icon="fc-plus-assets"
              extraClassNames="searchBtn"
              action={() => addAsset(false, !isAsset(item))}
              actionOnEnter={true}
              large={true}
              disabled={isEditingAssetGroup}
              testId="addAssetIcon"
              tooltip={!isEditingAssetGroup ? 'SEARCH_DATA.ADD_ASSET_TO_GROUP_TOOLTIP' : ''}
              tooltipDelay={500}
            />
          )}

          {/* assets and "items of items'" parents */}
          {(isAsset(item) || (!isAsset(item) && item.hasActiveChildren)) && !_.isEmpty(storeAssets) && (
            <IconWithManagedSpinner
              icon="fc-plus-selected"
              extraClassNames="searchBtn"
              action={() => addAsset(true)}
              actionOnEnter={true}
              large={true}
              disabled={isEditingAssetGroup}
              testId="addAssetIconSameChildrenOnly"
              tooltip={!isEditingAssetGroup ? 'SEARCH_DATA.ADD_ASSET_TO_GROUP_CHILDREN_MATCH_TOOLTIP' : ''}
              tooltipDelay={500}
            />
          )}

          {/* items (including items of items' parents) */}
          {!isAsset(item) && !isTableDefinition(item) && (
            <IconWithManagedSpinner
              icon="fa-plus-circle"
              extraClassNames="searchBtn"
              action={() => addAsset()}
              actionOnEnter={true}
              large={true}
              testId="addItemToAssetIcon"
              tooltip="SEARCH_DATA.ADD_ASSET_ADD_ITEM_TOOLTIP"
              tooltipDelay={500}
            />
          )}
        </>
      )}
    </>
  );
};
